//src/pages/inventory/Pharmacy/index.jsx:-
import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  styled,
  useTheme,
  Menu,
  MenuItem,
  TextField,
  Stack,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import ModeEditOutlineTwoToneIcon from "@mui/icons-material/ModeEditOutlineTwoTone";
import Refresh from "@mui/icons-material/Refresh";
import Header from "../../../components/Header.jsx";
import { useNavigate } from 'react-router-dom';
import { getAllPatients } from "../../../services/patientService.js";
import { getByPatientId } from "../../../services/PatientAssignmentService.js";
import { getAdmissionByPatientId } from "../../../services/PatientAdmissionService.js";
import { getPrescriptionsByPatientId } from "../../../services/prescriptionService.js";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext.jsx";
import { getOrganizationName } from "../../../utils/organizationUtils";
import { downloadStyledTableExcel } from "../../../utils/patientTableExcelExport";
import dayjs from "dayjs";
import { RemoveRedEye } from "@mui/icons-material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import DownloadIcon from "@mui/icons-material/Download";
import {
  TABLE_ICON_BUTTON_SX,
  TABLE_ICON_SX,
  TABLE_SEARCH_ROW_SX,
} from "../../../utils/pageLayoutStyles";

const columns = [
  { id: "id", label: "Id" },
  { id: "name", label: "Name" },
  { id: "age", label: "Age" },
  { id: "gender", label: "Gender" },
  { id: "aadhar", label: "Aadhar" },
  { id: "mobile", label: "Contact Number" },
  { id: "address", label: "Address" },
];

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
}));

function AllPatientsTable({ onPatientClick, hideMainTitle = false }) {
  const [page, setPage] = useState(0);
  const theme = useTheme();
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [fetching, setFetching] = useState({ patients_list: true });
  const [patients_list, setPatientList] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const location = useLocation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [filterName, setFilterName] = useState("");
  const [filterGender, setFilterGender] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  
  useEffect(() => {
    fetchPatientsList();
  }, [location.key]);

  const fetchPatientsList = async () => {
    setFetching({ ...fetching, patients_list: true });
    try {
      const data = await getAllPatients();
      const sorted = [...(data || [])].sort((a, b) => (b.id ?? 0) - (a.id ?? 0));
      setPatientList(sorted);
    } catch (error) {
      console.log(error);
    }
    setFetching({ ...fetching, patients_list: false });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleMenuClick = (event, patient) => {
    setAnchorEl(event.currentTarget);
    setSelectedPatient(patient);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedPatient(null);
  };

  const checkPatientType = async (patientId) => {
    try {
      // First, check for OPD prescriptions - if patient has OPD prescriptions, they are OPD
      const prescriptions = await getPrescriptionsByPatientId(patientId);
      if (prescriptions && Array.isArray(prescriptions) && prescriptions.length > 0) {
        // Patient has OPD prescriptions, so they're OPD
        return false;
      }
      
      // If no OPD prescriptions, check for active IPD admissions
      const admissions = await getAdmissionByPatientId(patientId);
      if (admissions && Array.isArray(admissions) && admissions.length > 0) {
        // Check if patient has any ACTIVE admission (status must be explicitly "admit", "admitted", or "admission")
        const hasActiveAdmission = admissions.some(admission => {
          const status = String(admission.status || "").toLowerCase().trim();
          // Only consider these specific statuses as active
          return status === 'admit' || status === 'admitted' || status === 'admission';
        });
        
        if (hasActiveAdmission) {
          return true; // Patient has active IPD admission
        }
      }
      
      // If no active admission and no prescriptions, default to OPD
      return false;
    } catch (error) {
      console.error("Error checking patient type:", error);
      // Default to OPD if error occurs
      return false;
    }
  };

  const handleIssueMedicine = async () => {
    if (selectedPatient) {
      const isIpdPatient = await checkPatientType(selectedPatient.id);
      if (onPatientClick) {
        // Use callback if provided (for in-page timeline display)
        onPatientClick(selectedPatient.id, isIpdPatient ? 'ipd' : 'opd');
      } else {
        // Fallback to navigation (for standalone usage)
        if (isIpdPatient) {
          navigate(`/ipd-treatments/${selectedPatient.id}`);
        } else {
          navigate(`/prescriptions/${selectedPatient.id}`);
        }
      }
    }
    handleClose();
  };

  const handleInvoice = () => {
    // Logic for creating an invoice
    console.log("Creating invoice for:", selectedPatient);
    handleClose();
  };

  const filteredRows = patients_list.filter((row) => {
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const values = [row.id, row.name, row.gender, row.age, row.aadhar, row.mobile, row.address]
        .filter(Boolean)
        .map((v) => String(v).toLowerCase());
      if (!values.some((v) => v.includes(q))) return false;
    }
    if (filterName.trim()) {
      const q = filterName.toLowerCase();
      const values = [row.id, row.name, row.gender, row.age, row.aadhar, row.mobile, row.address]
        .filter(Boolean)
        .map((v) => String(v).toLowerCase());
      if (!values.some((v) => v.includes(q))) return false;
    }
    if (filterGender) {
      if (String(row.gender || "") !== filterGender) return false;
    }
    return true;
  });

  const clearFilters = () => {
    setSearchQuery("");
    setFilterName("");
    setFilterGender("");
  };

  const handleExport = async () => {
    const rows = filteredRows || [];
    const headers = ["Id", "Name", "Gender", "Age"];
    const tableRows = rows.map((r) => [r.id, r.name, r.gender, r.age]);

    await downloadStyledTableExcel({
      filename: `pharmacy_patients_${dayjs().format("YYYY-MM-DD")}`,
      sheetName: "Pharmacy Patients",
      title: "Pharmacy Patients",
      organizationName: getOrganizationName(user),
      tableHeaders: headers,
      tableRows,
    });
  };

  return (
    <Grid container>
      {!hideMainTitle && (
        <Grid item xs={12} mb={0.5}>
          <Header title="PATIENTS" />
        </Grid>
      )}

      <Paper sx={{ width: "100%" }}>
        <Box sx={{ p: 1.5, pt: 1, pb: 1 }}>
          <Box sx={TABLE_SEARCH_ROW_SX}>
            <TextField
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={TABLE_ICON_SX} fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Tooltip title={showFilters ? "Hide filters" : "Show filters"}>
              <IconButton
                size="small"
                sx={TABLE_ICON_BUTTON_SX}
                onClick={() => setShowFilters((v) => !v)}
              >
                <FilterAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export to Excel">
              <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={handleExport}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton
                size="small"
                sx={TABLE_ICON_BUTTON_SX}
                onClick={async () => await fetchPatientsList()}
              >
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          {showFilters && (
          <Stack spacing={1} direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mt: 1 }}>
            <TextField size="small" label="Filter by any field" value={filterName} onChange={(e)=>setFilterName(e.target.value)} />
            <TextField size="small" label="Gender" value={filterGender} onChange={(e)=>setFilterGender(e.target.value)} select sx={{ minWidth: 160 }}>
              <MenuItem value="">All</MenuItem>
              {[...new Set(patients_list.map((p)=>p.gender).filter(Boolean))].map((g)=>(
                <MenuItem key={g} value={g}>{g}</MenuItem>
              ))}
            </TextField>
            <Tooltip title="Clear filters">
              <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={clearFilters}>
                <ClearAllIcon fontSize="small"/>
              </IconButton>
            </Tooltip>
          </Stack>
          )}
        </Box>
        <TableContainer sx={{ maxHeight: 550 }}>
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <StyledTableCell
                    key={column.id}
                    align={column.align}
                    style={{ top: 57, minWidth: column.minWidth }}
                  >
                    {column.label}
                  </StyledTableCell>
                ))}
                <StyledTableCell key={"action"} style={{ top: 57 }}>
                  Action
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.length === 0 || fetching.patients_list ? (
                <TableRow>
                  <TableCell colSpan={columns.length}>
                    {fetching.patients_list && <CircularProgress size={40} />}
                    {!fetching.patients_list && filteredRows.length === 0 && (
                      <Alert>No Patient </Alert>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => {
                    return (
                      <TableRow
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={row.id}
                      >
                        {columns.map((column) => {
                          const value = row[column.id];
                          return (
                            <TableCell key={column.id} align={column.align}>
                              {column.id === 'name' ? (
                                <span
                                  style={{
                                    color: '#568AF7',
                                    cursor: 'pointer',
                                  }}
                                  onClick={async () => {
                                    const isIpdPatient = await checkPatientType(row.id);
                                    if (onPatientClick) {
                                      // Use callback if provided (for in-page timeline display)
                                      onPatientClick(row.id, isIpdPatient ? 'ipd' : 'opd');
                                    } else {
                                      // Fallback to navigation (for standalone usage)
                                      if (isIpdPatient) {
                                        navigate(`/ipd-treatments/${row.id}`);
                                      } else {
                                        navigate(`/prescriptions/${row.id}`, { state: { tab: "0" } });
                                      }
                                    }
                                  }}
                                >
                                  {row.name}
                                </span>
                              ) : (
                                value
                              )}
                            </TableCell>
                          );
                        })}
                        <TableCell>
                          <IconButton onClick={(e) => handleMenuClick(e, row)}>
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem onClick={handleIssueMedicine}>
          Open Prescription
        </MenuItem>
        {/* <MenuItem onClick={handleInvoice}>
          Invoice
        </MenuItem> */}
      </Menu>
    </Grid>
  );
}

export default AllPatientsTable;
